import "./security.css";
import { TrashIcon } from "@phosphor-icons/react";
import { useRef, useState, type Ref } from "react";
import type { AccountSecurityManagement } from "../types";
import { authenticationActionError } from "./presentation";

export type AccountDeletionState =
  | "completed"
  | "confirming"
  | "deleting"
  | "failed"
  | "freshness-required"
  | "idle"
  | "signed-out";

export function AccountDeletionControl({
  accountSecurity,
}: {
  accountSecurity: AccountSecurityManagement;
}) {
  const actionRef = useRef<HTMLButtonElement>(null);
  const [confirmation, setConfirmation] = useState("");
  const [state, setState] = useState<AccountDeletionState>("idle");

  function cancel(): void {
    setConfirmation("");
    setState("idle");
    queueMicrotask(() => actionRef.current?.focus());
  }

  async function remove(): Promise<void> {
    setState("deleting");
    try {
      await accountSecurity.deleteAccount();
      setState("completed");
    } catch (cause) {
      const message = authenticationActionError(
        cause,
        "Account deletion could not finish.",
      );
      setState(
        /sign in again/i.test(message) ? "freshness-required" : "failed",
      );
    }
  }

  async function recoverFreshness(): Promise<void> {
    setState("deleting");
    try {
      await accountSecurity.requestFreshSignIn();
      cancel();
    } catch {
      setState("freshness-required");
    }
  }

  return (
    <AccountDeletionCard
      actionRef={actionRef}
      confirmation={confirmation}
      onCancel={cancel}
      onConfirm={() => void remove()}
      onConfirmationChange={setConfirmation}
      onFreshSignIn={() => void recoverFreshness()}
      onRequest={() => setState("confirming")}
      onRetry={() => {
        setConfirmation("");
        setState("confirming");
      }}
      state={state}
    />
  );
}

export function AccountDeletionCard({
  actionRef,
  confirmation = "",
  onCancel = () => undefined,
  onConfirm = () => undefined,
  onConfirmationChange = () => undefined,
  onFreshSignIn = () => undefined,
  onRequest = () => undefined,
  onRetry = () => undefined,
  state,
}: {
  actionRef?: Ref<HTMLButtonElement>;
  confirmation?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
  onConfirmationChange?: (value: string) => void;
  onFreshSignIn?: () => void;
  onRequest?: () => void;
  onRetry?: () => void;
  state: AccountDeletionState;
}) {
  return (
    <section className="pm-passport-card pm-account-deletion-card">
      <header>
        <div>
          <h2>Delete Account</h2>
          <p>Permanently remove your identity and private Puzzleini data.</p>
        </div>
        <small>Permanent</small>
      </header>

      {state === "confirming" ? (
        <div
          aria-label="Confirm Account deletion"
          aria-modal="true"
          className="pm-security-confirmation pm-account-deletion-confirmation"
          onKeyDown={(event) => {
            if (event.key === "Escape") onCancel();
          }}
          role="dialog"
        >
          <div>
            <strong>This cannot be undone.</strong>
            <p>
              Your sign-in methods, sessions, profile, Friendships, progression,
              owned uploads, and private history access will be removed. Shared
              Game Records remain with a neutral Deleted player identity.
            </p>
            <label>
              <span>TYPE DELETE TO CONFIRM</span>
              <input
                aria-label="Type DELETE to confirm"
                autoComplete="off"
                autoFocus
                onChange={(event) =>
                  onConfirmationChange(event.currentTarget.value)
                }
                value={confirmation}
              />
            </label>
          </div>
          <button onClick={onCancel} type="button">
            Cancel
          </button>
          <button
            className="danger"
            disabled={confirmation !== "DELETE"}
            onClick={onConfirm}
            type="button"
          >
            Delete Account permanently
          </button>
        </div>
      ) : state === "deleting" ? (
        <div className="pm-security-state" role="status">
          <strong>Deleting your Account…</strong>
          <span>
            Keep this page open while private data and sign-in access are
            removed.
          </span>
          <button disabled type="button">
            Deletion in progress…
          </button>
        </div>
      ) : state === "completed" || state === "signed-out" ? (
        <p className="pm-security-feedback success" role="status">
          {state === "completed"
            ? "Your Account was deleted. You are signed out of Puzzleini."
            : "This Account is deleted and signed out."}
        </p>
      ) : (
        <>
          <div className="pm-data-export-action pm-account-deletion-action">
            <span className="pm-auth-method-icon">
              <TrashIcon aria-hidden="true" size={21} weight="duotone" />
            </span>
            <div>
              <strong>
                {state === "freshness-required"
                  ? "Recent sign-in required"
                  : state === "failed"
                    ? "Deletion did not finish"
                    : "Leave this puzzle universe"}
              </strong>
              <p>
                A manual erasure path remains available if self-service fails.
              </p>
            </div>
            <button
              aria-label={
                state === "freshness-required"
                  ? "Sign in again"
                  : state === "failed"
                    ? "Try deletion again"
                    : "Delete my Account"
              }
              onClick={
                state === "freshness-required"
                  ? onFreshSignIn
                  : state === "failed"
                    ? onRetry
                    : onRequest
              }
              ref={actionRef}
              type="button"
            >
              {state === "freshness-required"
                ? "Sign in again"
                : state === "failed"
                  ? "Try again"
                  : "Delete my Account"}
            </button>
          </div>
          {state === "freshness-required" ? (
            <p className="pm-security-feedback error" role="alert">
              Sign in again before deleting your Account.
            </p>
          ) : state === "failed" ? (
            <div className="pm-account-deletion-fallback">
              <p className="pm-security-feedback error" role="alert">
                Deletion did not finish. Some private data may already be
                removed. Retry safely or use the erasure request path in the
                Privacy Policy.
              </p>
              <a href="/privacy">Open Privacy Policy</a>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
